import { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "@fontsource-variable/roboto-mono/index.css";
import { Provider } from "react-redux";
import { store } from "./store/store";
import {
  firebaseConfig,
  sentryReplayErrorSampleRate,
  sentryReplaySessionSampleRate,
  sentryStandaloneRequestSampleRate,
  sentryVideoPlaybackSampleRate,
  VIDEO_PLAYBACK_SPAN_OP,
} from "./constants";
import * as Sentry from "@sentry/react";
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  initializeAuth,
} from "firebase/auth";
import { ProvideStatusNotifications } from "contexts/status";
import { AppRoutes } from "./routes/app";
import { isLabellingToolPath } from "./routes/labellingToolPath";
import { ErrorNotificationMonitor } from "components/ErrorNotificationMonitor";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { HotkeyToggleProvider } from "contexts/hotkeys";
import { setServerVersion } from "store/slices/versionSlice";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { useLanguage } from "hooks/settings";
import { setupTranslations } from "components/settings/i18n";
import { OfflineDetectorDialog } from "components/ConnectionDialog/OfflineDetectorDialog";
import { ConfirmDialogProvider } from "contexts/confirmDialog";
import { PromptDialogProvider } from "contexts/promptDialog";
import { HotkeysProvider } from "react-hotkeys-hook";
import { LoadingScreenProvider } from "contexts/globalLoadingScreen";
import { GlobalErrorFallback } from "components/common/error/GlobalErrorFallback";

const container = document.getElementById("root");

setupTranslations();

const LocalizationProviderWithLocale = ({
  children,
}: {
  children: ReactNode;
}) => {
  const language = useLanguage();
  const mappedLanguage = ["en-gb"].includes(language)
    ? language
    : language.slice(0, 2).toLowerCase();
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={mappedLanguage}
    >
      {children}
    </LocalizationProvider>
  );
};
const extractTenantFromPath = () => {
  const pathParts = window.location.pathname.split("/");
  const tenant = pathParts[1];
  return tenant && tenant !== "sign-in" && tenant !== "" ? tenant : null;
};

async function startApp() {
  if (container !== null) {
    const isLabellingTool = isLabellingToolPath(location.pathname);
    // Clark renders production artefacts in headless Chromium.
    const isUntracedHeadlessBrowser =
      import.meta.env.VITE_SENTRY_ENVIRONMENT !== "prod" &&
      navigator.userAgent.includes("HeadlessChrome");

    // The labelling tool's video players stop the recorder.
    if (!isLabellingTool) {
      const { tryLoadAndStartRecorder } =
        await import("@alwaysmeticulous/recorder-loader");
      await tryLoadAndStartRecorder({
        recordingToken: import.meta.env.VITE_METICULOUS_PROJECT_ID,
      });
    }

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release: `scrap-chef-frontend@${import.meta.env.VITE_RELEASE}`,
      integrations: [
        Sentry.reactRouterBrowserTracingIntegration({
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        // Replays leave text, inputs, and media unmasked and capture details for
        // same-origin network requests.
        ...(isLabellingTool
          ? []
          : [
              Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [window.location.origin],
              }),
            ]),
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.feedbackIntegration({
          colorScheme: "system",
          showBranding: false,
          autoInject: false,
        }),
      ],
      // With span streaming, each fetch or XHR outside a page load or navigation
      // span becomes its own root span. Video playback makes many such requests.
      tracesSampler: ({ attributes, inheritOrSampleWith }) => {
        if (isUntracedHeadlessBrowser) {
          return 0;
        }
        switch (attributes["sentry.op"]) {
          case "http.client":
            return sentryStandaloneRequestSampleRate;
          case VIDEO_PLAYBACK_SPAN_OP:
            return sentryVideoPlaybackSampleRate;
          default:
            return inheritOrSampleWith(1.0);
        }
      },
      profileSessionSampleRate: 1.0,
      profileLifecycle: "trace",
      replaysSessionSampleRate: sentryReplaySessionSampleRate,
      replaysOnErrorSampleRate: sentryReplayErrorSampleRate,
      maxBreadcrumbs: 100,
      attachStacktrace: true,
      // Outlook Safe Links scanners raise this when they open emailed links.
      ignoreErrors: ["Object Not Found Matching Id"],
      beforeSend(event) {
        if (!event.tags?.tenant) {
          const tenant = extractTenantFromPath();
          if (tenant) {
            event.tags = { ...event.tags, tenant };
          }
        }
        return event;
      },
      beforeSendSpan(span) {
        const tenant = extractTenantFromPath();
        if (!span.is_segment || !tenant) {
          return span;
        }
        return {
          ...span,
          name: span.name.startsWith(`/${tenant}`)
            ? span.name.replace(`/${tenant}`, "/:tenant")
            : span.name,
          attributes: { tenant, ...span.attributes },
        };
      },
    });

    const firebaseApp = initializeApp(firebaseConfig);
    initializeAuth(firebaseApp, {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });

    const checkVersion = async () => {
      try {
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT === "development") {
          return;
        }

        const response = await fetch("/version.txt");
        const version = await response.text();

        store.dispatch(setServerVersion(version.trim()));
      } catch (error) {
        console.warn("Failed to fetch version:", error);
      }
    };
    setInterval(() => {
      void checkVersion();
    }, 60_000);

    const root = createRoot(container, {
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
      }),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    });

    root.render(
      <BrowserRouter>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <Sentry.ErrorBoundary
              fallback={(props) => (
                <GlobalErrorFallback error={props.error as Error} />
              )}
              onError={(error, componentStack) => {
                console.error("ErrorBoundary caught:", error);
                console.error("Component stack:", componentStack);
              }}
              handled={false}
            >
              <LoadingScreenProvider>
                <LocalizationProviderWithLocale>
                  <HotkeyToggleProvider>
                    <HotkeysProvider>
                      <ProvideStatusNotifications>
                        <ConfirmDialogProvider>
                          <PromptDialogProvider>
                            <OfflineDetectorDialog />
                            <ErrorNotificationMonitor />
                            <AppRoutes />
                          </PromptDialogProvider>
                        </ConfirmDialogProvider>
                      </ProvideStatusNotifications>
                    </HotkeysProvider>
                  </HotkeyToggleProvider>
                </LocalizationProviderWithLocale>
              </LoadingScreenProvider>
            </Sentry.ErrorBoundary>
          </ThemeProvider>
        </Provider>
      </BrowserRouter>
    );
  }
}

void startApp();
export { getBase64 } from "./helpers";
